import {
    CoreDI,
    DeviceType,
    DI,
    IAnimation,
    GameConfig,
    inject,
    LAYERS,
    PayAction,
    ReelConstants,
    ReelsOrdering,
    SymbolsOrdering,
    Timer,
    WinConfigAnimation,
    WinPresentationView,
    WinPresentationViewEvents
} from '@gongxigames/framework/slot';
import {Layer} from '@pixi/layers';
import {Graphics} from 'pixi.js';
import {GameWinPresentationUtils} from 'src/winPresentation/GameWinPresentationUtils';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {WolfHavenSpin} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenSpin';
import {GameReelConstants} from 'src/gameReels/GameReelConstants';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import Position = Pay.Position;

export class GameWinPresentationView extends WinPresentationView {
    @inject(DI.WinPresentationUtils)
    protected _winPresentationUtils!: GameWinPresentationUtils;

    protected configureSymbol(payAction: PayAction, symbol: number, pos: Position, dimension: number = GameReelConstants.DEFAULT_DIMENSION): IAnimation {
        const animation = this._winPresentationUtils.createSymbolAnimation(payAction, symbol, pos, '', dimension);

        this._updater.add(animation);
        this.container.addChild(animation.container);
        animation.start();

        return animation;
    }

    protected reorderWinSymbols(positions: Position[]): Position[] {
        const winSymbols = [...positions];
        const getOrderIndex = (symbolId: number) => {
            return this._gameConfig.reelsConfig.defaultSymbolsOrder.findIndex((orderID) => {
                return this.isSymbolsEqual(orderID, symbolId);
            });
        };
        const rowOrder = (a: Pay.Position, b: Pay.Position) => {
            if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.TOP_TO_BOTTOM) {
                return a.row - b.row;
            } else if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.BOTTOM_TO_TOP) {
                return b.row - a.row;
            }
        };
        const reelOrder = (a: Pay.Position, b: Pay.Position) => {
            if (ReelConstants.REELS_ORDERING === ReelsOrdering.LEFT_TO_RIGHT) {
                return a.reel - b.reel;
            } else if (ReelConstants.REELS_ORDERING === ReelsOrdering.RIGHT_TO_LEFT) {
                return b.reel - a.reel;
            }
        };

        winSymbols.sort((a, b) => {
            const symbolOrder = getOrderIndex(a.symbolId) - getOrderIndex(b.symbolId);
            const rowOrdering = rowOrder(a, b);

            if (symbolOrder) {
                return symbolOrder;
            } else if (rowOrdering && a.reel === b.reel) {
                return rowOrdering;
            }

            return reelOrder(a, b) || 0;
        });

        return winSymbols;
    }

    protected cacheAnimations(): void {
        const allDimension = [3, 4, 5];
        const utils = this._winPresentationUtils;
        const createCachedAnimation = (animation: WinConfigAnimation) => {
            if (animation.spine && animation.cached) {
                allDimension.forEach((dimension) => {
                    this.spineUtils.createCachedAnimation(utils.getNameWithDimension(animation.spine!, dimension), animation.animation);
                });
            }
        };

        if (this._gameConfig.winConfig.normal && this._gameConfig.winConfig.normal.regular[0]) {
            for (const animation of this._gameConfig.winConfig.normal.regular[0].animation) {
                createCachedAnimation(animation);
            }
        }
    }

    protected createMask(): Graphics | undefined {
        const ADDITIONAL_HEIGHT = 371;
        const mask = new Graphics();

        mask.beginFill(0xFFFFFF);
        mask.drawRect(0, 0, 1, 1);
        mask.endFill();
        mask.width = this._gameConfig.viewport.desktop.width;
        mask.height = ReelConstants.SYMBOL_SLOT.height * ReelConstants.ROW_AMOUNT + ADDITIONAL_HEIGHT;

        return mask;
    }

    protected configureInitialAnimationSymbols(): void {
        const spinData = this._currentRestoreSpin ? this._currentRestoreSpin : this._logic.spinData;

        this.configureSymbols(this._pays, this._winPresentationUtils.getWinConfig().initial, undefined, (spinData as WolfHavenSpin).getDimension());
        this.emit(WinPresentationViewEvents.INITIAL_ANIMATION_STARTED, this._pays);
    }

    protected configurePayRestore(pay: Pay, payAction: PayAction): void {
        const spin = this._currentRestoreSpin as WolfHavenSpin;

        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, spin.symbolIds, spin.getDimension());
        this._updater.add(new Timer(this.playPayAnimation.bind(this, true), payAction.time, true));
    }

    protected configurePay(pay: Pay, payAction: PayAction): void {
        const spin = this._logic.spinData as WolfHavenSpin;

        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, spin.symbolIds, spin.getDimension());
        this._updater.add(new Timer(this.playPayAnimation.bind(this), payAction.time, true));
    }

    protected configurePayOneCycle(pay: Pay, payAction: PayAction): void {
        const spin = this._logic.spinData as WolfHavenSpin;

        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, spin.symbolIds, spin.getDimension());
        this._updater.add(new Timer(this.playPaysAnimationOneCycle.bind(this), payAction.time, true));
    }

    protected configureSymbols(pays: Pay[], payAction: PayAction, symbolIds?: number[][], dimension?: number[]): void {
        this.getWinSymbols(pays).forEach(pos => {
            this.configureSymbol(payAction, symbolIds ? symbolIds[pos.reel][pos.row] : pos.symbolId, pos, dimension ? dimension[pos.reel] : undefined);
        });
    }

    public play(): void {
        this._currentPay = 0;
        this._allWinHighlightsShown = false;
        this._allPayAnimationsFinishedEventEmitted = false;
        this._winTypeConfig = this._winPresentationUtils.getWinTypeConfig()!;
        this._pays = this.getPays(this._logic.spinData.pays);
        this._payActions = this._winPresentationUtils.parseWinActions(this._pays);

        // add mask in order to prevent showing animations under the bottom bar
        if (this.isPlatform(DeviceType.DESKTOP)) {
            this.addMask();
        }
        this.playInitialAnimation();

        if (this.isFreeSpinsWon() || this._logic.isRetriggerWon()) {
            this.soundPlayer.play('picktrigger');
        } else if (this.isPickBonusWon()) {
            this.soundPlayer.play('freespintrigger');
            this.soundPlayer.playMusic('pickloop');
        } else {
            this.playWinSound();
        }
    }

    protected isPickBonusWon(): boolean {
        return this._logic.nextAction === Action.PICK && this._logic.previousAction === Action.SPIN;
    }

    public static layer: Layer = LAYERS.REELS_WIN;
    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;
}
